import React, { useState, useEffect, useRef } from 'react';
import { uploadFile, AccessPolicyRoute } from '@/utils/upload';
import Toast from './Toast';

export interface ImageUploadBoxProps {
  /** 字段标签 */
  label?: string;
  /** 提示或补充文字（展示在标签右侧） */
  tip?: string;
  /** 是否必填 */
  required?: boolean;
  /** 绑定的 asset ID 或 URL 资源值 */
  value?: string;
  /** 值发生改变时的回调 */
  onChange?: (assetId: string, file?: File) => void;
  /** 上传场景，默认为 'SUBJECT' */
  scene?: Api.Upload.UploadScene;
  /** 显式指定访问策略路由 */
  policy?: AccessPolicyRoute;
  /** 接收的文件扩展名/MIME */
  accept?: string;
  /** 占位主标题 */
  placeholder?: string;
  /** 占位副标题 */
  subPlaceholder?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 容器额外样式类名 */
  className?: string;
  /** 显式说明/强制是否为视频类型 */
  isVideo?: boolean;
  /** 显式说明/强制是否为 PDF 类型 */
  isPdf?: boolean;
  /** 显式说明/强制是否为文档类型 (PDF, DOC, DOCX 等) */
  isDocument?: boolean;
  /** 回显的文件名 */
  fileName?: string;
}

function getFileNameFromUrl(url: string): string {
  if (!url) return '';
  try {
    const cleanUrl = url.split('?')[0];
    const segments = cleanUrl.split('/');
    const last = segments[segments.length - 1];
    if (last && (last.includes('.') || last.length > 3)) {
      return decodeURIComponent(last);
    }
  } catch {
    // ignore parsing errors
  }
  return '';
}

export const ImageUploadBox: React.FC<ImageUploadBoxProps> = ({
  label,
  tip,
  required = false,
  value = '',
  onChange,
  scene = 'SUBJECT',
  policy,
  accept = 'image/*',
  placeholder = '点击或拖拽上传文件',
  subPlaceholder = '支持常见视频、图片或 PDF 格式',
  disabled = false,
  className = '',
  isVideo: isVideoProp,
  isPdf: isPdfProp,
  isDocument: isDocProp,
  fileName: fileNameProp,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [imageError, setImageError] = useState(false);
  const [preview, setPreview] = useState<{
    url: string;
    isPdf: boolean;
    isDoc: boolean;
    isVideo: boolean;
    name?: string;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // 依赖 update
  useEffect(() => {
    setImageError(false);
    if (!value) {
      setPreview(null);
      return;
    }

    const cleanUrl = value.split('?')[0].toLowerCase();
    const targetStr = (value + ' ' + (fileNameProp || '')).toLowerCase();
    const isUrl = value.startsWith('http') || value.startsWith('blob:') || value.startsWith('/');

    const isImg = isLikelyImage(value, fileNameProp);

    const isVid =
      isVideoProp !== undefined
        ? isVideoProp
        : accept.includes('video') || /\.(mp4|mov|webm|avi|mkv|flv|m4v)($|\?)/i.test(cleanUrl) || cleanUrl.includes('video');

    const isPdfFile =
      isPdfProp !== undefined
        ? isPdfProp
        : !isImg && (/\.(pdf)($|\?)/i.test(cleanUrl) || targetStr.includes('.pdf'));

    const isDocFile =
      isDocProp !== undefined
        ? isDocProp
        : !isImg &&
          !isPdfFile &&
          (/\.(doc|docx|wps|odt|txt)($|\?)/i.test(cleanUrl) ||
            targetStr.includes('.doc') ||
            targetStr.includes('.docx'));

    setPreview((prev) => {
      const urlToUse = isUrl ? value : (prev?.url || '');
      return {
        url: urlToUse,
        isPdf: isPdfFile,
        isDoc: isDocFile,
        isVideo: isVid,
        name: fileNameProp || (isUrl ? getFileNameFromUrl(value) : (prev?.name || '')),
      };
    });
  }, [value, isVideoProp, isPdfProp, isDocProp, fileNameProp, accept]);

  const handleFileSelect = async (file: File) => {
    setUploading(true);
    setError('');
    setToastMsg('');
    setImageError(false);

    const isImg = file.type.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg|bmp|ico)$/i.test(file.name);
    const isPdf = !isImg && (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'));
    const isDoc =
      !isImg &&
      !isPdf &&
      (/\.(doc|docx|wps|odt|txt)$/i.test(file.name) ||
        file.type.includes('word') ||
        file.type.includes('document'));
    const isVid =
      isVideoProp !== undefined
        ? isVideoProp
        : file.type.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv|flv|m4v)$/i.test(file.name);

    const objectUrl = URL.createObjectURL(file);

    try {
      const res = await uploadFile(file, scene, policy);
      const assetId = String(res.asset_id || res.upload_asset_id || '');
      if (assetId) {
        setPreview({
          url: res.file_url || objectUrl,
          isPdf,
          isDoc,
          isVideo: isVid,
          name: file.name,
        });
        onChange?.(assetId, file);
      } else {
        const msg = '上传失败，未返回有效资源 ID';
        setError(msg);
        setToastMsg(msg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '文件上传失败，请重试';
      setError(msg);
      setToastMsg(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPreview(null);
    setError('');
    setToastMsg('');
    setImageError(false);
    onChange?.('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerSelect = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  // 是否为图片格式 URL 或类型
  const isLikelyImage = (url?: string, name?: string) => {
    if (accept.includes('image') && !isPdfProp && !isDocProp && !isVideoProp) {
      return true;
    }
    if (!url && !name) return false;
    const str = ((url || '') + ' ' + (name || '')).split('?')[0].toLowerCase();
    if (/\.(png|jpg|jpeg|gif|webp|svg|bmp|ico)($|\?)/i.test(str)) {
      return true;
    }
    if (url && (url.startsWith('blob:') || url.startsWith('http') || url.startsWith('/')) && !str.includes('.pdf') && !str.includes('.doc')) {
      return true;
    }
    return false;
  };

  const isImg = preview && !imageError && isLikelyImage(preview.url, preview.name);

  const isDocumentView =
    preview &&
    !isImg &&
    (preview.isPdf ||
      preview.isDoc ||
      isDocProp ||
      (imageError && (isPdfProp || isDocProp)));

  return (
    <div className={className}>
      {label && (
        <label className="block text-xs font-medium text-foreground-700 mb-2">
          {required && <span className="text-red-500 mr-1">*</span>}
          {label}
          {tip && <span className="text-foreground-400 font-normal ml-1">{tip}</span>}
        </label>
      )}

      <div className="relative border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-primary-300/60 transition-colors bg-white">
        {uploading ? (
          <div className="space-y-2 py-4">
            <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin mb-1 block"></i>
            <p className="text-sm font-medium text-foreground-700">文件上传中...</p>
          </div>
        ) : value && preview ? (
          <div className="space-y-3 relative z-10">
            {preview.isVideo ? (
              <div className="space-y-2 max-w-lg mx-auto">
                <video
                  src={preview.url}
                  controls
                  preload="metadata"
                  className="max-h-56 w-full mx-auto rounded-lg shadow-sm border border-gray-200 bg-black/90 object-contain"
                />
                <p className="text-xs text-foreground-600 font-medium truncate">{preview.name || '已上传视频文件'}</p>
              </div>
            ) : isDocumentView ? (
              <div className="p-4 rounded-xl bg-slate-50 hover:bg-slate-100/80 transition-colors border border-slate-200/90 flex items-center justify-between gap-4 max-w-md mx-auto text-left shadow-xs">
                <div className="flex items-center gap-3 overflow-hidden min-w-0">
                  {preview.isPdf ? (
                    <div className="w-11 h-11 rounded-xl bg-red-50 text-red-500 flex items-center justify-center shrink-0 border border-red-100/80 shadow-2xs">
                      <i className="ri-file-pdf-2-fill text-2xl"></i>
                    </div>
                  ) : preview.isDoc ? (
                    <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100/80 shadow-2xs">
                      <i className="ri-file-word-2-fill text-2xl"></i>
                    </div>
                  ) : (
                    <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0 border border-amber-100/80 shadow-2xs">
                      <i className="ri-file-text-fill text-2xl"></i>
                    </div>
                  )}

                  <div className="truncate min-w-0">
                    <p className="text-xs sm:text-sm font-semibold text-foreground-900 truncate" title={preview.name || '已上传附件文档'}>
                      {preview.name || (preview.isPdf ? '已上传 PDF 简历/文档' : preview.isDoc ? '已上传 Word 简历/文档' : '已上传附件文档')}
                    </p>
                    <p className="text-[11px] text-foreground-400 mt-0.5 flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      {preview.isPdf ? 'PDF 文档' : preview.isDoc ? 'Word 文档' : '附件文档'}
                    </p>
                  </div>
                </div>

                {preview.url && (preview.url.startsWith('http') || preview.url.startsWith('blob:')) && (
                  <a
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="px-3 py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 bg-white hover:bg-primary-50 border border-gray-200 rounded-lg shrink-0 transition-all shadow-2xs flex items-center gap-1"
                  >
                    <i className="ri-external-link-line"></i>
                    查看/下载
                  </a>
                )}
              </div>
            ) : (
              <div className="relative inline-block max-w-full">
                {preview.url ? (
                  <img
                    src={preview.url}
                    alt={label || '文件预览'}
                    onError={() => setImageError(true)}
                    className="max-h-56 mx-auto rounded-lg object-contain shadow-sm border border-gray-100"
                  />
                ) : (
                  <div className="p-4 rounded-lg bg-primary-50/50 border border-primary-100 flex flex-col items-center justify-center gap-2 max-w-xs mx-auto">
                    <i className="ri-image-2-line text-3xl text-primary-500"></i>
                    <p className="text-xs text-foreground-700 font-medium">海报/图片已上传 (ID: {value})</p>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-center gap-4 pt-1">
              <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                <i className="ri-checkbox-circle-fill text-green-500"></i> 上传成功
              </span>
              <button
                type="button"
                onClick={triggerSelect}
                className="text-xs text-primary-600 hover:text-primary-500 font-medium hover:underline cursor-pointer"
              >
                重新上传
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="text-xs text-red-500 hover:text-red-600 font-medium hover:underline cursor-pointer"
              >
                删除文件
              </button>
            </div>
          </div>
        ) : (
          <div className="py-3 cursor-pointer" onClick={triggerSelect}>
            <i className="ri-upload-cloud-2-line text-3xl text-foreground-400 mb-2 block"></i>
            <p className="text-sm font-medium text-foreground-700 mb-1">{placeholder}</p>
            {subPlaceholder && <p className="text-xs text-foreground-400">{subPlaceholder}</p>}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          disabled={disabled || uploading}
          required={required && !value}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              handleFileSelect(file);
            }
          }}
        />
      </div>
      <Toast message={toastMsg} type="error" onClose={() => setToastMsg('')} />
    </div>
  );
};

export default ImageUploadBox;

